'use client';

import React from 'react';
import classnames from 'classnames';

import Iconography, { IconographyProps } from '../Iconography/Iconography';
import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import Skeleton from '../Skeleton/Skeleton';

import styles from './Pill.module.scss';

type Theme = 'dark' | 'light';
type Color = 'brand' | 'neutral';
type Size = 'small' | 'medium' | 'large';
type Type = 'primary' | 'secondary' | 'tertiary';
type Direction = 'ltr' | 'rtl';
type Target = '_self' | '_blank' | '_top' | '_parent';

export interface PillProps {
  label: string;
  href?: string;
  target?: Target;
  gaComponent?: string;
  icon?: Pick<IconographyProps, 'category' | 'name' | 'size' | 'color'>;
  linkContext?: string;
  handleClick?: () => void;
  dataTestId?: string;
  color?: Color;
  theme?: Theme;
  size?: Size;
  type?: Type;
  direction?: Direction;
  disabled?: boolean;
  showSkeleton?: boolean;
}

const Pill: React.FC<PillProps> = ({
  label,
  href = '',
  target = '_self',
  gaComponent = 'Pill',
  icon,
  linkContext,
  handleClick,
  dataTestId = 'Pill',
  color = 'brand',
  theme = 'dark',
  size = 'small',
  type = 'primary',
  direction = 'ltr',
  disabled = false,
  showSkeleton = false,
}) => {
  if (showSkeleton) {
    return <Skeleton className={classnames(styles.pill, styles[size])} theme={theme} />;
  }

  const handleOnClick = () => {
    if (typeof window !== `undefined`) {
      const dataLayerMap: Record<string, unknown> = {
        event: 'interaction',
        interaction__type: href ? 'hyperlink' : 'cta',
        interaction__text: label,
        interaction__component: gaComponent,
      };

      if (href) dataLayerMap.interaction__destination = href;
      if (linkContext) dataLayerMap.interaction__linkContext = linkContext;

      (window as unknown as { dataLayer?: unknown[] })?.dataLayer?.push(dataLayerMap);
    }

    if (handleClick) {
      handleClick();
    }
  };

  const commonProps = {
    className: classnames(styles.pill, styles[size], styles[`${theme}_${color}_${type}`], {
      [styles.disabled]: disabled,
    }),
    'data-testid': dataTestId,
    onClick: handleOnClick,
  };

  const content = (
    <>
      <Typography variant="body" size="small" direction={direction}>
        {label}
      </Typography>
      {icon && (
        <Box>
          <Iconography {...icon} />
        </Box>
      )}
    </>
  );

  return href ? (
    <a {...commonProps} href={href} target={target}>
      {content}
    </a>
  ) : (
    <button {...commonProps} type="button" disabled={disabled}>
      {content}
    </button>
  );
};

Pill.displayName = 'Pill';

export default Pill;
